import React, { useMemo, useState } from "react";
import { CONTACTS, contactKey } from "./contactsData";


const CATEGORY_COLORS = {
  "City Hall": { bg: "#0ea5e9", text: "#fff" },
  "Police Services": { bg: "#8b5cf6", text: "#fff" },
  Library: { bg: "#22c55e", text: "#06210c" },
  "Public School": { bg: "#f59e0b", text: "#231a03" },
  Private: { bg: "#ef4444", text: "#fff" },
};

function Badge({ category }) {
  const c = CATEGORY_COLORS[category] || { bg: "#64748b", text: "#fff" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      fontSize: ".75rem", fontWeight: 700, padding: "4px 10px",
      borderRadius: 999, background: c.bg, color: c.text,
      boxShadow: "0 6px 16px rgba(0,0,0,.25)", letterSpacing: ".2px",
    }}>{category}</span>
  );
}

function ItemCard({ it }) {
  return (
    <div style={{
      background: "linear-gradient(180deg, rgba(255,255,255,.03), rgba(255,255,255,.015))",
      border: "1px solid rgba(148,163,184,.18)", borderRadius: 16,
      padding: 16, boxShadow: "0 10px 30px rgba(2,6,23,.35)"}}
    >
      <h3 style={{ margin: "6px 0" }}>{it.name}</h3>
      {it.address && <p style={{ margin: 0 }}>{it.address}</p>}
      {it.phone && <p style={{ margin: 0 }}><strong style={{ color: "#94a3b8" }}>Phone:</strong> {it.phone}</p>}
      {it.attendance && <p style={{ margin: 0 }}><strong style={{ color: "#94a3b8" }}>Attendance:</strong> {it.attendance}</p>}
      {it.email && <p style={{ margin: 0 }}><strong style={{ color: "#94a3b8" }}>Email:</strong> <a href={`mailto:${it.email}`} style={{ color: "#60a5fa" }}>{it.email}</a></p>}
      {it.principal && <p style={{ margin: 0 }}><strong style={{ color: "#94a3b8" }}>Principal:</strong> {it.principal}</p>}
      {it.people && (
        <ul style={{ margin: "8px 0", paddingLeft: 18 }}>
          {it.people.map((p, i) => (
            <li key={i}>{p.title} — {p.name}{p.phone ? ` (${p.phone})` : ""}{p.email ? ` — ${p.email}` : ""}</li>
          ))}
        </ul>
      )}
      <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
        {it.website && <a href={it.website} target="_blank" rel="noopener noreferrer" style={{ color: "#22d3ee" }}>Website</a>}
        {it.map && <a href={it.map} target="_blank" rel="noopener noreferrer" style={{ color: "#60a5fa" }}>Map</a>}
      </div>
    </div>
  );
}

function Section({ section }) {
  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <Badge category={section.category} />
        <h2 style={{ margin: 0 }}>{section.category}</h2>
      </div>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
        {section.items.map((it, i) => <ItemCard key={i} it={it} />)}
      </div>
    </section>
  );
}

export default function CityContactsPage({ state = "CA", city = "Dana Point" }) {
  const [q, setQ] = useState("");
  const key = useMemo(() => contactKey(state, city), [state, city]);
  const data = CONTACTS[key];

  const filteredSections = useMemo(() => {
    if (!data) return [];
    const qs = q.trim().toLowerCase();
    if (!qs) return data.sections;
    const match = (txt) => String(txt || "").toLowerCase().includes(qs);
    return data.sections
      .map((s) => ({
        ...s,
        items: s.items.filter((it) =>
          [it.name, it.address, it.phone, it.principal, it.email, it.attendance, ...(it.people || []).flatMap(p => [p.title, p.name, p.phone, p.email])]
            .some(match)
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [data, q]);

  if (!data) {
    return (
      <div style={{ color: "#e5e7eb", background: "#0f172a", minHeight: "100vh", padding: 24 }}>
        <h1 style={{ fontSize: "1.8rem" }}>No data for {city}, {state}</h1>
        <p style={{ color: "#94a3b8" }}>Key tried: {key}. Add it to CONTACTS to enable this page.</p>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "system-ui, -apple-system, Segoe UI, Roboto", background: "#0f172a", color: "#e5e7eb", minHeight: "100vh", padding: 24 }}>
      <header style={{ textAlign: "center", marginBottom: 24 }}>
        <h1 style={{ fontSize: "2rem", fontWeight: 800 }}>{data.city} — Official Contacts</h1>
        <p style={{ color: "#94a3b8" }}>{data.state}</p>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, category, person, address…"
          style={{ marginTop: 12, padding: "10px 14px", borderRadius: 999, border: "1px solid #334155", width: "100%", maxWidth: 520 }}
        />
      </header>

      <main style={{ maxWidth: 1100, margin: "0 auto" }}>
        {filteredSections.length === 0 ? (
          <p style={{ color: "#94a3b8" }}>No results. Try another search.</p>
        ) : (
          filteredSections.map((s, i) => <Section key={i} section={s} />)
        )}
      </main>

      
    </div>
  );
}

// ===================== Usage Examples =====================
// 1) Plain React (no router):
// <CityContactsPage state="CA" city="Laguna Beach" />
//
// 2) React Router (v6+) route driven by URL like "/ca/laguna-beach":
// import { useParams } from "react-router-dom";
// function RoutedCityContacts() {
//   const { state, city } = useParams();
//   return <CityContactsPage state={state} city={city} />;
// }
// <Routes>
//   <Route path=":state/:city" element={<RoutedCityContacts />} />
// </Routes>
